import { useEffect, useRef } from 'react';
import { api } from '@/lib/api';

/**
 * Send a usage heartbeat every 60 s while the tab is visible.
 *
 * The backend bumps the user's daily usage bucket by exactly 60 s per ping and
 * enforces a 45 s minimum interval — so this hook can be lazy / imprecise
 * without skewing analytics. Pings stop the moment the tab is hidden and
 * resume on visibility / focus.
 */
const INTERVAL_MS = 60_000;

export default function useUsageHeartbeat(enabled = true) {
  const timerRef = useRef(null);
  const inflightRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const send = async () => {
      if (inflightRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      inflightRef.current = true;
      try {
        await api.post('/usage/heartbeat');
      } catch {
        // Network blip is fine — next interval re-tries.
      } finally {
        inflightRef.current = false;
      }
    };

    // Fire one immediately so a quick visit (<60 s) is still credited.
    send();
    timerRef.current = setInterval(send, INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') send();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled]);
}
